import { useState } from "react";
import { Edit, Loader2, Plus, Power, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGrowthData } from "@/hooks/growth/useGrowthData";

export function CouponsPanel({
  data,
}: {
  activeCompanyId?: string | null;
  data: ReturnType<typeof useGrowthData>;
}) {
  const { coupons, services, saveCoupon, toggleCoupon } = data;

  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<any>(null);
  const [cCode, setCCode] = useState("");
  const [cDesc, setCDesc] = useState("");
  const [cType, setCType] = useState<"PERCENT" | "FIXED">("PERCENT");
  const [cValue, setCValue] = useState<number>(10);
  const [cService, setCService] = useState<string>("");

  const openCouponModal = (c?: any) => {
    if (c) {
      setEditingCoupon(c);
      setCCode(c.code);
      setCDesc(c.description);
      setCType(c.type);
      setCValue(c.value);
      setCService(c.restricted_service || "");
    } else {
      setEditingCoupon(null);
      setCCode("");
      setCDesc("");
      setCType("PERCENT");
      setCValue(10);
      setCService("");
    }
    setCouponModalOpen(true);
  };

  const handleSave = () => {
    saveCoupon.mutate(
      {
        id: editingCoupon?.id,
        code: cCode,
        description: cDesc,
        type: cType,
        value: cValue,
        restricted_service: cService,
      },
      {
        onSuccess: () => setCouponModalOpen(false),
      },
    );
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-500/10 text-blue-500 rounded-xl">
            <Tag size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-100">
              Campanhas e Cupons
            </h2>
            <p className="text-sm text-slate-400">
              Crie códigos promocionais para retenção.
            </p>
          </div>
        </div>
        <Dialog open={couponModalOpen} onOpenChange={setCouponModalOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => openCouponModal()}
              className="bg-blue-600 hover:bg-blue-700 font-bold gap-2"
            >
              <Plus size={18} /> Novo Cupom
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[450px] bg-slate-900 border-slate-700 text-slate-100">
            <DialogHeader>
              <DialogTitle>
                {editingCoupon ? "Editar Cupom" : "Criar Novo Cupom"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-slate-300">Código do Cupom</Label>
                <Input
                  value={cCode}
                  onChange={(e) => setCCode(e.target.value.toUpperCase())}
                  placeholder="Ex: VOLTA10"
                  className="bg-slate-800 border-slate-700 uppercase font-bold"
                />
              </div>
              <div>
                <Label className="text-slate-300">Descrição / Motivo</Label>
                <Input
                  value={cDesc}
                  onChange={(e) => setCDesc(e.target.value)}
                  placeholder="Ex: Campanha de Inverno"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label className="text-slate-300">Tipo de Desconto</Label>
                  <select
                    value={cType}
                    onChange={(e) => setCType(e.target.value as any)}
                    className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-300"
                  >
                    <option value="PERCENT">Porcentagem (%)</option>
                    <option value="FIXED">Valor Fixo (R$)</option>
                  </select>
                </div>
                <div className="w-1/3">
                  <Label className="text-slate-300">Valor</Label>
                  <Input
                    type="number"
                    value={cValue}
                    onChange={(e) => setCValue(Number(e.target.value))}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">
                  Restringir a um Serviço Específico? (Opcional)
                </Label>
                <select
                  value={cService}
                  onChange={(e) => setCService(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-300 mt-1"
                >
                  <option value="">Nenhuma restrição (Aplica no Total)</option>
                  {services.map((s: any) => (
                    <option key={s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 font-bold"
                onClick={handleSave}
                disabled={saveCoupon.isPending || !cCode || !cValue}
              >
                {saveCoupon.isPending ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  "Salvar Cupom"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-950 text-slate-400">
            <tr>
              <th className="p-4 rounded-tl-lg font-semibold">Código</th>
              <th className="p-4 font-semibold">Valor</th>
              <th className="p-4 font-semibold">Restrição</th>
              <th className="p-4 font-semibold">Status</th>
              <th className="p-4 rounded-tr-lg font-semibold text-right">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50 text-slate-300">
            {coupons.map((c: any) => (
              <tr
                key={c.id}
                className="hover:bg-slate-800/30 transition-colors"
              >
                <td className="p-4 font-bold text-slate-100">
                  {c.code}
                  <br />
                  <span className="text-xs text-slate-500 font-normal">
                    {c.description}
                  </span>
                </td>
                <td className="p-4 font-bold text-emerald-400">
                  {c.type === "PERCENT" ? `${c.value}%` : `R$ ${c.value}`}
                </td>
                <td className="p-4 text-slate-400">
                  {c.restricted_service || "Total da OS"}
                </td>
                <td className="p-4">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      c.status === "ATIVO"
                        ? "bg-emerald-500/10 text-emerald-500"
                        : "bg-slate-700 text-slate-400"
                    }`}
                  >
                    {c.status}
                  </span>
                </td>
                <td className="p-4 text-right flex justify-end gap-2">
                  <Button
                    size="icon"
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    onClick={() => openCouponModal(c)}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    onClick={() => toggleCoupon.mutate(c)}
                  >
                    <Power
                      size={16}
                      className={
                        c.status === "ATIVO"
                          ? "text-rose-500"
                          : "text-emerald-500"
                      }
                    />
                  </Button>
                </td>
              </tr>
            ))}
            {coupons.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="p-8 text-center text-slate-500 border border-slate-800 border-dashed rounded-xl"
                >
                  Nenhum cupom de desconto criado ainda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
